import { TriangleAlert } from 'lucide-react';
import { useId, useRef, type ReactNode } from 'react';

import { useFocusTrap } from '../lib/useFocusTrap';
import { Button } from './Button';

interface Props {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'default';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmation for destructive actions. Cancel receives focus first. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useFocusTrap(ref, onCancel);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="relative w-full max-w-sm rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50"
      >
        <div className="flex gap-3">
          {tone === 'danger' && (
            <div className="grid size-8 shrink-0 place-items-center rounded-full bg-crit-soft text-crit">
              <TriangleAlert aria-hidden="true" className="size-4" strokeWidth={2} />
            </div>
          )}
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-fg">
              {title}
            </h2>
            <div id={bodyId} className="mt-1 text-[13px] leading-relaxed text-fg-muted">
              {children}
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onCancel} data-autofocus>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={busy}
            className={tone === 'danger' ? 'bg-crit! hover:bg-crit/90!' : ''}
          >
            {busy ? 'Aguarde…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
